// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useEffect, useState, useCallback, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 2 * 60 * 1000; // warn 2 minutes before expiry

export function useSessionTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiryRef = useRef<number>(Date.now() + TIMEOUT_MS);

  const doLogout = useCallback(async () => {
    clearAll();
    try { await signOut(auth); } catch { /* ignore */ }
    navigate("/login");
  }, [navigate]);

  const clearAll = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const resetTimer = useCallback(() => {
    clearAll();
    setShowWarning(false);
    expiryRef.current = Date.now() + TIMEOUT_MS;

    // Warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSec(Math.ceil(WARNING_MS / 1000));
      countdownRef.current = setInterval(() => {
        const left = Math.max(0, Math.ceil((expiryRef.current - Date.now()) / 1000));
        setRemainingSec(left);
        if (left <= 0 && countdownRef.current) clearInterval(countdownRef.current);
      }, 1000);
    }, TIMEOUT_MS - WARNING_MS);

    // Logout timer
    timerRef.current = setTimeout(doLogout, TIMEOUT_MS);
  }, [doLogout]);

  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    let throttled = false;
    const handler = () => {
      if (throttled) return;
      throttled = true;
      setTimeout(() => { throttled = false; }, 1000);
      if (!showWarning) resetTimer();
    };
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      clearAll();
    };
    // eslint-disable-next-line
  }, []);

  return { showWarning, remainingSec, dismissWarning, doLogout };
}
